// src/utils/markdownLoader.js
import matter from 'gray-matter';
import { contentConfig, CACHE_EXPIRY } from '../config/contentConfig';
import { Buffer } from 'buffer'; // eslint-disable-line no-unused-vars
// 在某些环境中，可能需要全局 Buffer
if (typeof window !== 'undefined' && typeof window.Buffer === 'undefined') {
  window.Buffer = Buffer;
}


const contentCache = {
  projects: {},
  experiences: {},
  education: {},
  personalInfo: null,
  allContent: null,
  manifest: null,
  timestamp: {}
};

function isCacheValid(type, id = null) {
  const now = Date.now();
  const key = id ? `${type}_${id}` : type;
  return contentCache.timestamp[key] && 
         (now - contentCache.timestamp[key]) < CACHE_EXPIRY;
}

const defaultData = {
  project: {
    name: '未命名项目',
    description: '',
    responsibilities: [],
    technologies: [], // 确保有默认空数组
    textureUrl: '/assets/textures/default_project_texture.jpg'
  },
  experience: {
    company: '未知公司',
    title: '未知职位',
    startDate: '',
    endDate: '',
    location: '',
    responsibilities: [],
    achievements: [],
    skills: [], // 确保有默认空数组
    textureUrl: '/assets/textures/default_experience_texture.jpg'
  },
  education: {
    institution: '未知院校',
    degree: '',
    field: '',
    startDate: '',
    endDate: '',
    courses: [],
    achievements: [],
    skills: [], // 确保有默认空数组
    textureUrl: '/assets/textures/default_education_texture.jpg'
  },
  personal: {
    name: '',
    title: '',
    email: '',
    phone: '',
    location: '',
    summary: '',
    contact: {},
    skills: [], // 确保有默认空数组
    textureUrl: '/assets/textures/default_personal_texture.jpg'
  }
};

export function parseMarkdown(content, type = 'project', markdownFilePath = '') {
  try {
    const isHtml = content.trim().startsWith('<!DOCTYPE html>') || content.trim().startsWith('<html');
    if (isHtml) {
      console.error(`错误: 收到HTML内容而不是Markdown文件！路径: ${markdownFilePath}`);
      throw new Error(`加载到的是HTML而不是Markdown文件！请检查路径配置。文件路径: ${markdownFilePath}`);
    }
    
    const { data, content: markdownContent } = matter(content);
    
    // 1. 自动提取第一张图片作为纹理
    let autoTextureUrl = defaultData[type]?.textureUrl || `/assets/textures/default_texture.jpg`; 
    const imageRegex = /!\s*\[.*?\]\s*\((.*?)\s*(\s*".*?")?\)/;
    const firstImageMatch = markdownContent.match(imageRegex);

    if (firstImageMatch && firstImageMatch[1]) {
      let extractedUrl = firstImageMatch[1];
      extractedUrl = extractedUrl.replace(/\/\/+/g, '/'); 

      if (extractedUrl.startsWith('http://') || extractedUrl.startsWith('https://')) {
        autoTextureUrl = extractedUrl;
      } else if (extractedUrl.startsWith('/')) { 
        autoTextureUrl = extractedUrl.startsWith('/public/') ? extractedUrl.substring('/public'.length) : extractedUrl;
      } else if (markdownFilePath) { 
        try {
          const baseOrigin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost'; 
          const baseUrl = new URL(markdownFilePath.startsWith('/') ? markdownFilePath.substring(1) : markdownFilePath, baseOrigin).href;
          const resolvedUrl = new URL(extractedUrl, baseUrl);
          autoTextureUrl = resolvedUrl.pathname; 
          
          if (autoTextureUrl.startsWith('/public/')) { 
            autoTextureUrl = autoTextureUrl.substring('/public'.length);
          }
          // console.log(`相对路径 '${firstImageMatch[1]}' 基于 '${markdownFilePath}' 解析为: ${autoTextureUrl}`);
        } catch (e) {
          console.error(`无法解析图片路径 '${firstImageMatch[1]}' 相对于 '${markdownFilePath}'. 错误: ${e.message}`);
          let fallbackUrl = '/' + extractedUrl.replace(/^[\/\\]+/, ''); 
          if (fallbackUrl.startsWith('/public/')) {
            fallbackUrl = fallbackUrl.substring('/public'.length);
          }
          autoTextureUrl = fallbackUrl;
        }
      } else { 
         let fallbackUrl = '/' + extractedUrl.replace(/^[\/\\]+/, '');
         if (fallbackUrl.startsWith('/public/')) {
            fallbackUrl = fallbackUrl.substring('/public'.length);
          }
        autoTextureUrl = fallbackUrl;
      }
    }

    // 2. 从 Markdown 正文提取 #tag 格式的标签
    const tagRegex = /#([a-zA-Z0-9_\u4e00-\u9fa5]+)/g; // 支持中文、英文、数字、下划线
    let autoExtractedTags = [];
    let tagMatch;
    while ((tagMatch = tagRegex.exec(markdownContent)) !== null) {
      autoExtractedTags.push(tagMatch[1]);
    }
    if (autoExtractedTags.length > 0) {
      console.log(`从 ${markdownFilePath} 正文提取到标签: ${autoExtractedTags.join(', ')}`);
    }
    
    return { 
      frontmatter: data, 
      content: markdownContent, 
      textureUrl: autoTextureUrl,
      extractedTags: autoExtractedTags // 返回提取的标签
    };
  } catch (error) {
    console.error(`解析Markdown内容失败 (类型: ${type}, 路径: ${markdownFilePath}):`, error);
    return { 
      frontmatter: defaultData[type] || {}, 
      content: `解析错误: ${error.message}`, 
      textureUrl: defaultData[type]?.textureUrl || `/assets/textures/error_texture.jpg`,
      extractedTags: [] // 确保错误情况下也返回空数组
    };
  }
}

async function loadContentManifest() {
  if (isCacheValid('manifest') && contentCache.manifest) {
    return contentCache.manifest;
  }
  try {
    const manifestUrl = '/content-manifest.json';
    const response = await fetch(manifestUrl);
    if (!response.ok) {
      throw new Error(`获取内容清单失败 (${response.status}) 从 ${manifestUrl}`);
    }
    const manifestData = await response.json();
    contentCache.manifest = manifestData;
    contentCache.timestamp['manifest'] = Date.now();
    return manifestData;
  } catch (error) {
    console.error('加载内容清单时出错:', error);
    return { projects: [], experiences: [], education: [] };
  }
}

async function loadMarkdownItem(itemType, folderName, markdownFileName) {
  const cacheKey = `${itemType}_${folderName}`; 
  if (isCacheValid(itemType, folderName) && contentCache[itemType] && contentCache[itemType][folderName]) {
    return contentCache[itemType][folderName];
  }

  try {
    const basePath = contentConfig[itemType]?.path;
    if (!basePath) throw new Error(`类型 '${itemType}' 的路径配置未找到。`);

    const contentPath = `/${basePath}/${folderName}/${markdownFileName}`;
    // console.log(`尝试加载 ${itemType}: ${contentPath}`);
    const response = await fetch(contentPath);
    if (!response.ok) {
      throw new Error(`加载 ${itemType} '${folderName}/${markdownFileName}' 失败 (${response.status})，路径: ${contentPath}`);
    }
    const contentText = await response.text();
    const parsed = parseMarkdown(contentText, itemType, contentPath); 
    
    // 确定最终的 technologies 或 skills 数组
    let finalTagsOrSkills = [];
    const frontmatterSpecificField = (itemType === 'projects') ? parsed.frontmatter.technologies : parsed.frontmatter.skills;
    const frontmatterGenericTags = parsed.frontmatter.tags;

    if (frontmatterSpecificField && Array.isArray(frontmatterSpecificField) && frontmatterSpecificField.length > 0) {
      finalTagsOrSkills = frontmatterSpecificField;
    } else if (frontmatterGenericTags && Array.isArray(frontmatterGenericTags) && frontmatterGenericTags.length > 0) {
      finalTagsOrSkills = frontmatterGenericTags;
    } else if (parsed.extractedTags && Array.isArray(parsed.extractedTags) && parsed.extractedTags.length > 0) {
      finalTagsOrSkills = parsed.extractedTags;
    }
    // 如果 finalTagsOrSkills 仍为空数组, itemData 将使用 defaultData 或 frontmatter 中可能存在的空数组

    const itemData = { 
      id: folderName, 
      ...defaultData[itemType], 
      ...parsed.frontmatter, 
      content: parsed.content,
      textureUrl: parsed.textureUrl
    };

    // 将计算得到的 finalTagsOrSkills 赋值给正确的字段
    if (itemType === 'projects') {
      itemData.technologies = finalTagsOrSkills;
    } else { // experiences, education, personal
      itemData.skills = finalTagsOrSkills;
    }
    
    // 如果 frontmatter 中有 relatedProjects (主要用于 experience)，它会通过 ...parsed.frontmatter 保留下来

    if (!contentCache[itemType]) contentCache[itemType] = {};
    contentCache[itemType][folderName] = itemData;
    contentCache.timestamp[cacheKey] = Date.now();
    return itemData;
  } catch (error) {
    console.error(`加载 ${itemType} '${folderName}' 失败:`, error);
    return {
      error: true,
      errorMessage: `加载 ${itemType} '${folderName}' 失败: ${error.message}`,
      id: folderName,
      name: `加载失败的 ${itemType} ${folderName}`, 
      ...(defaultData[itemType] || {}), 
      textureUrl: defaultData[itemType]?.textureUrl || `/assets/textures/default_${itemType}_texture.jpg`
    };
  }
}

export async function loadProjectMarkdown(folderName, markdownFileName) {
  return loadMarkdownItem('projects', folderName, markdownFileName);
}

export async function loadExperienceMarkdown(folderName, markdownFileName) {
  return loadMarkdownItem('experiences', folderName, markdownFileName);
}

export async function loadEducationMarkdown(folderName, markdownFileName) {
  return loadMarkdownItem('education', folderName, markdownFileName);
}

async function loadAllItemsOfType(itemType, loadSingleItemFunction) {
  try {
    const manifest = await loadContentManifest();
    const itemsToLoad = manifest[itemType] || []; 
    
    if (!itemsToLoad.length) {
      console.warn(`内容清单中未找到 '${itemType}' 条目，或清单加载失败。`);
      return [];
    }
    
    // console.log(`将从清单加载 '${itemType}':`, itemsToLoad.map(item => `${item.folder}/${item.file}`));
    const loadedItems = await Promise.all(
      itemsToLoad.map(item => loadSingleItemFunction(item.folder, item.file)) 
    );
    
    const validItems = loadedItems.filter(item => item && !item.error);
    console.log(`成功加载 ${validItems.length} / ${itemsToLoad.length} 个 '${itemType}' 数据 (基于清单)`);
    return validItems;
  } catch (error) {
    console.error(`加载所有 '${itemType}' 数据时出错:`, error);
    return [];
  }
}

export async function loadAllProjects() {
  return loadAllItemsOfType('projects', loadProjectMarkdown);
}

export async function loadAllExperiences() {
  const experiences = await loadAllItemsOfType('experiences', loadExperienceMarkdown);
  return experiences.map(exp => {
    if (exp.error) return exp; 
    const validationErrors = [];
    if (!exp.company || exp.company === defaultData.experience.company) validationErrors.push('公司名称缺失');
    if (!exp.title || exp.title === defaultData.experience.title) validationErrors.push('职位名称缺失');
    if (!exp.startDate) validationErrors.push('开始日期缺失');
    // 职责内容现在由 frontmatter 或正文标签填充 skills，不在此处强制检查 responsibilities 数组
    // if (!exp.responsibilities || exp.responsibilities.length === 0) validationErrors.push('职责内容缺失');
    
    if (validationErrors.length > 0) {
      console.warn(`经历 ${exp.id} 数据验证警告:`, validationErrors);
      return { ...exp, validationWarnings: validationErrors };
    }
    return { ...exp, validated: true };
  });
}

export async function loadAllEducation() {
  const educationList = await loadAllItemsOfType('education', loadEducationMarkdown);
  return educationList.map(edu => {
    if (edu.error) return edu;
    const validationErrors = [];
    if (!edu.institution || edu.institution === defaultData.education.institution) validationErrors.push('院校名称缺失');
    if (!edu.degree || !edu.degree.trim()) validationErrors.push('学位信息缺失');
    if (!edu.startDate) validationErrors.push('开始日期缺失');
    
    if (validationErrors.length > 0) {
      console.warn(`教育背景 ${edu.id} 数据验证警告:`, validationErrors);
      return { ...edu, validationWarnings: validationErrors };
    }
    return { ...edu, validated: true };
  });
}

export async function loadPersonalInfo() {
  const cacheKey = 'personalInfo';
  if (isCacheValid(cacheKey) && contentCache.personalInfo) {
    return contentCache.personalInfo;
  }
  try {
    const contentPath = `/${contentConfig.personal.path}`; 
    const response = await fetch(contentPath);
    if (!response.ok) {
      throw new Error(`加载个人信息失败 (${response.status})`);
    }
    const content = await response.text();
    const parsed = parseMarkdown(content, 'personal', contentPath); 
    const personalData = {
      ...defaultData.personal,
      ...parsed.frontmatter,
      content: parsed.content,
      textureUrl: parsed.textureUrl,
      skills: parsed.frontmatter.skills || parsed.extractedTags || defaultData.personal.skills // 确保 personalInfo 也有 skills
    };
    contentCache.personalInfo = personalData;
    contentCache.timestamp[cacheKey] = Date.now();
    return personalData;
  } catch (error) {
    console.error('加载个人信息失败:', error);
    return {
      error: true,
      errorMessage: `加载个人信息失败: ${error.message}`,
      name: '加载失败的个人信息',
      ...defaultData.personal
    };
  }
}

export async function loadAllContent() {
  const [personalInfo, experiences, projects, education] = await Promise.all([
    loadPersonalInfo(),
    loadAllExperiences(),
    loadAllProjects(),
    loadAllEducation()
  ]);
  
  return {
    personalInfo,
    experiences,
    projects,
    education
  };
}